import React, { useRef } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { Field, Form as FinalForm } from 'react-final-form';
import { Form, LocationAutocompleteInput } from '../../../../components';
import IconProduct from '../Icons/IconProduct';
import IconLocation from '../Icons/IconLocation';
import IconSearch from '../Icons/IconSearch';

import css from './SearchGroup.module.css';

const identity = v => v;
const MAX_MOBILE_SCREEN_WIDTH = 768;

const KeywordSearchField = props => {
  const { keywordSearchWrapperClasses, intl, isMobile = false, inputRef } = props;
  return (
    <div className={keywordSearchWrapperClasses}>
      <Field
        name="keywords"
        render={({ input, meta }) => {
          return (
            <input
              className={css.desktopInputKeyword}
              {...input}
              id={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              data-testid={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              ref={inputRef}
              type="text"
              placeholder={
                isMobile
                  ? intl.formatMessage({
                      id: 'SearchGroup.keywordPlaceholderMobile',
                    })
                  : intl.formatMessage({
                      id: 'SearchGroup.keywordPlaceholderDesktop',
                    })
              }
              autoComplete="off"
            />
          );
        }}
      />
    </div>
  );
};

const LocationSearchField = props => {
  const { desktopInputRootClass, intl, isMobile = false, inputRef, onLocationChange } = props;
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        const searchOnChange = value => {
          onChange(value);
          onLocationChange?.(value);
        };

        return (
          <LocationAutocompleteInput
            className={desktopInputRootClass}
            iconClassName={css.desktopIcon}
            inputClassName={css.desktopInput}
            predictionsClassName={css.desktopPredictions}
            predictionsAttributionClassName={null}
            placeholder={
              isMobile
                ? intl.formatMessage({ id: 'SearchGroup.locationPlaceholderMobile' })
                : intl.formatMessage({ id: 'SearchGroup.locationPlaceholderDesktop' })
            }
            closeOnBlur={!isMobile}
            inputRef={inputRef}
            input={{ ...restInput, onChange: searchOnChange }}
            meta={meta}
          />
        );
      }}
    />
  );
};

const SearchGroup = props => {
  const searchInpuRef = useRef(null);
  const intl = useIntl();
  const { appConfig, onSubmit, ...restOfProps } = props;

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;

  const onKeywordSubmit = values => {
    onSubmit({
      keywords: values.keywords,
      location: values.location,
    });
    searchInpuRef?.current?.blur();
  };

  return (
    <FinalForm
      {...restOfProps}
      onSubmit={onKeywordSubmit}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          desktopInputRoot,
          handleSubmit,
        } = formRenderProps;
        const classes = classNames(rootClassName, className);
        const desktopInputRootClass = desktopInputRoot || css.desktopInputRoot;

        const keywordSearchWrapperClasses = classNames(
          css.keywordSearchWrapper,
          isMobileLayout ? css.mobileInputRoot : desktopInputRootClass
        );

        return (
          <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="SearchPage">
            <div className={css.searchGroup}>
              <div className={css.keywordSearch}>
                <IconProduct />
                <KeywordSearchField
                  keywordSearchWrapperClasses={keywordSearchWrapperClasses}
                  intl={intl}
                  isMobile={isMobileLayout}
                  inputRef={searchInpuRef}
                />
              </div>
              <div className={css.keywordSearch}>
                <IconLocation />
                <LocationSearchField
                  desktopInputRootClass={css.desktopInputRoot}
                  intl={intl}
                  isMobile={isMobileLayout}
                  inputRef={searchInpuRef}
                />
              </div>
              <button type="submit" className={css.searchSubmit}>
                <div className={css.searchSubmitText}>
                  <IconSearch />
                  <FormattedMessage id="SearchGroup.searchSubmit" />
                </div>
              </button>
            </div>
          </Form>
        );
      }}
    />
  );
};

export default SearchGroup;
